import { createContext, useContext, useEffect, useState } from "react"
import { getAuthStorage, clearAuthStorage } from "@/lib/authStorage"

export type EffectivePermissions = Record<string, string[]>

type CurrentUser = {
  id: string
  name: string
  email: string
  tenant_id: string
  doctor_id?: string
  clinic_id: string
  role: string
  // Permission system fields (populated after login)
  is_owner?: boolean
  job_role?: string | null
  role_template_id?: string | null
  effective_permissions?: EffectivePermissions
  tenant_plan?: string
  is_platform_admin?: boolean
  must_change_password?: boolean
}

type AuthContextType = {
  currentUser: CurrentUser | null
  setCurrentUser: (user: CurrentUser) => void
  logout: () => void
  isLoading: boolean
  tenantId: string | null
  refreshPermissions: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'

async function fetchPermissions(token: string): Promise<Partial<CurrentUser>> {
  try {
    const res = await fetch(`${BASE_URL}/permissions/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return {}
    const data = await res.json()
    return {
      is_owner:              data.is_owner,
      job_role:              data.job_role ?? null,
      role_template_id:      data.role_template_id ?? null,
      effective_permissions: data.effective_permissions ?? {},
      tenant_plan:           data.tenant_plan ?? 'hospital',
      is_platform_admin:     !!data.is_platform_admin,
      must_change_password:  !!data.must_change_password,
    }
  } catch {
    return {}
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUserState] = useState<CurrentUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function initAuth() {
      try {
        const store = getAuthStorage()
        const stored = store.getItem("currentUser")
        if (!stored) { setIsLoading(false); return }

        const user = JSON.parse(stored) as CurrentUser
        if (!user || !user.id || !user.clinic_id || !user.role) {
          store.removeItem("currentUser")
          setIsLoading(false)
          return
        }
        if (!user.tenant_id) user.tenant_id = 'default_tenant'

        const token = store.getItem("accessToken")
        if (!token) { clearAuthStorage(); setIsLoading(false); return }

        // Validate token and fetch permissions in parallel
        const [res, perms] = await Promise.all([
          fetch(`${BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetchPermissions(token),
        ])
        if (cancelled) return

        if (res.status === 401 || res.status === 403) {
          // Access token expired — try to refresh silently before forcing login
          const { refreshAccessToken } = await import('@/lib/api')
          const refreshed = await refreshAccessToken()
          if (refreshed && !cancelled) {
            const newToken = store.getItem('accessToken')
            if (newToken) {
              const retryRes = await fetch(`${BASE_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` },
              })
              if (retryRes.ok && !cancelled) {
                const newPerms = await fetchPermissions(newToken)
                setCurrentUserState({ ...user, ...newPerms })
                return
              }
            }
          }
          // Refresh also failed — session truly expired
          clearAuthStorage()
          setCurrentUserState(null)
        } else if (res.ok) {
          if (!cancelled) setCurrentUserState({ ...user, ...perms })
        } else {
          clearAuthStorage()
          setCurrentUserState(null)
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error initializing auth:", error)
          clearAuthStorage()
          setCurrentUserState(null)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    initAuth()
    return () => { cancelled = true }
  }, [])

  const setCurrentUser = (user: CurrentUser) => {
    try {
      if (!user.tenant_id) user.tenant_id = 'default_tenant'
      const store = getAuthStorage()
      // Persist base user (no permission fields — those are refreshed from API)
      store.setItem("currentUser", JSON.stringify(user))
      setCurrentUserState(user)
      // Hydrate permissions async after login
      const token = store.getItem("accessToken")
      if (token) {
        fetchPermissions(token).then(perms => {
          setCurrentUserState(prev => prev ? { ...prev, ...perms } : prev)
        })
      }
    } catch (error) {
      console.error("Error saving user to auth storage:", error)
    }
  }

  const refreshPermissions = async () => {
    const store = getAuthStorage()
    const token = store.getItem("accessToken")
    if (!token || !currentUser) return
    const perms = await fetchPermissions(token)
    setCurrentUserState(prev => prev ? { ...prev, ...perms } : prev)
  }

  const logout = () => {
    try {
      clearAuthStorage()
      setCurrentUserState(null)
      window.location.href = "/login"
    } catch {
      window.location.href = "/login"
    }
  }

  const tenantId = currentUser?.tenant_id || null

  return (
    <AuthContext.Provider value={{ currentUser, setCurrentUser, logout, isLoading, tenantId, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used within an AuthProvider")
  return context
}
